import { motion } from "framer-motion";
import { Check, Crown, Shield, Target, Zap, Users, DollarSign, Star, Phone, Video, Mail, BarChart3, Sparkles } from "lucide-react";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { PageTour } from "@/components/tour/PageTour";
import { ventureHillsOverviewSteps } from "@/lib/tour-steps";

export default function VentureHillsOverview() {
  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageTour steps={ventureHillsOverviewSteps} />
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Venture Hills Overview</h1>
        <p className="text-muted-foreground">Company information and detailed package offerings</p>
      </div>

      {/* Package Offerings */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Standard Package */}
        <VentureCard className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl" />
          <VentureCardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Zap className="w-6 h-6 text-blue-400" />
              </div>
              <VentureCardTitle>Standard Package</VentureCardTitle>
            </div>
          </VentureCardHeader>
          <VentureCardContent className="space-y-6">
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold">$3,150</span>
              <span className="text-muted-foreground">setup</span>
              <span className="text-2xl font-bold ml-4">+ $150</span>
              <span className="text-muted-foreground">/demo</span>
            </div>
            
            {/* Lead Generation & Advertising */}
            <div className="space-y-3 pt-4 border-t border-border">
              <h4 className="font-semibold flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-blue-400" />
                Lead Generation & Advertising
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>Meta (Facebook & Instagram) Ads campaign management</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>Professional ad creative design - 15 high-converting creatives included</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>Campaign setup, optimization, and ongoing management</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>A/B testing of ad creatives and copy</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>Target audience research and segmentation</span>
                </div>
              </div>
            </div>

            {/* Lead Qualification */}
            <div className="space-y-3 pt-4 border-t border-border">
              <h4 className="font-semibold flex items-center gap-2">
                <Phone className="w-4 h-4 text-blue-400" />
                Lead Qualification
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>U.S.-based professional call team for lead qualification</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>Comprehensive lead scoring and filtering</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>CRM lead capture and management via GoHighLevel</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>Lead nurturing sequences via email and SMS</span>
                </div>
              </div>
            </div>

            {/* Demo Booking */}
            <div className="space-y-3 pt-4 border-t border-border">
              <h4 className="font-semibold flex items-center gap-2">
                <Video className="w-4 h-4 text-blue-400" />
                Demo Booking & Management
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>Automated demo booking calendar integration</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>Reminder sequences (email + SMS) to reduce no-shows</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>Post-demo follow-up automation</span>
                </div>
              </div>
            </div>

            {/* Reporting */}
            <div className="space-y-3 pt-4 border-t border-border">
              <h4 className="font-semibold flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-400" />
                Reporting & Analytics
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>Weekly performance reports delivered every Monday</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>Real-time dashboard access to all metrics</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>Monthly strategy calls with your account manager</span>
                </div>
              </div>
            </div>

            {/* Ideal For */}
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <h4 className="font-semibold mb-2">Ideal For:</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• SaaS companies with $50K+ MRR</li>
                <li>• Single-channel growth focus</li>
                <li>• Teams looking for predictable demo flow</li>
              </ul>
            </div>
          </VentureCardContent>
        </VentureCard>

        {/* Elite Package */}
        <VentureCard className="relative overflow-hidden border-amber-500/30">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-3xl" />
          <VentureCardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Crown className="w-6 h-6 text-amber-400" />
              </div>
              <VentureCardTitle>Elite Package</VentureCardTitle>
              <BadgeVenture variant="warning">Most Popular</BadgeVenture>
            </div>
          </VentureCardHeader>
          <VentureCardContent className="space-y-6">
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold">$5,150</span>
              <span className="text-muted-foreground">setup</span>
              <span className="text-2xl font-bold ml-4">+ $200</span>
              <span className="text-muted-foreground">/demo</span>
            </div>
            
            <p className="text-sm text-amber-400 font-medium">Everything in Standard, PLUS:</p>

            {/* Multi-Channel Advertising */}
            <div className="space-y-3 pt-4 border-t border-border">
              <h4 className="font-semibold flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-amber-400" />
                Multi-Channel Advertising
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>Meta (Facebook & Instagram) Ads</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>Google Ads (Search & Display)</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>LinkedIn Ads (B2B targeting)</span>
                </div>
                <div className="flex items-start gap-3">
                  <Star className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="font-medium">UNLIMITED high-converting creative production</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>Advanced retargeting campaigns across all platforms</span>
                </div>
              </div>
            </div>

            {/* Human Sales Closers */}
            <div className="space-y-3 pt-4 border-t border-border">
              <h4 className="font-semibold flex items-center gap-2">
                <Users className="w-4 h-4 text-amber-400" />
                Human Sales Closers
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-3">
                  <Star className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="font-medium">Professional closers trained for YOUR product</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>Closers handle entire demo → close process</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>You only talk to HOT leads ready to buy</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>Replace underperforming closers at no cost</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>Recorded demos for training and QA</span>
                </div>
              </div>
            </div>

            {/* Advanced Technology */}
            <div className="space-y-3 pt-4 border-t border-border">
              <h4 className="font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-400" />
                Advanced Technology
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>AI-powered lead scoring and prioritization</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>Automated re-engagement for lost leads</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>Multi-touch attribution tracking</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>Predictive analytics for deal closing</span>
                </div>
              </div>
            </div>

            {/* Premium Support */}
            <div className="space-y-3 pt-4 border-t border-border">
              <h4 className="font-semibold flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-400" />
                Premium Support
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-3">
                  <Star className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="font-medium">Dedicated account manager (not shared)</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>Priority Slack channel for instant communication</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>Bi-weekly strategy sessions (not monthly)</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>Direct line to Venture Hills founders</span>
                </div>
              </div>
            </div>

            {/* Ideal For */}
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <h4 className="font-semibold mb-2">Ideal For:</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• SaaS companies with $100K+ MRR (Healthcare & Construction focus)</li>
                <li>• Companies ready to scale aggressively</li>
                <li>• Teams wanting white-glove service</li>
                <li>• Businesses seeking true growth partnership</li>
              </ul>
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>

      {/* Performance Model */}
      <VentureCard>
        <VentureCardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-secondary/20">
              <DollarSign className="w-6 h-6 text-secondary" />
            </div>
            <VentureCardTitle>Pricing Structure</VentureCardTitle>
          </div>
        </VentureCardHeader>
        <VentureCardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Goodwill Demos</p>
              <p className="text-xl font-bold text-secondary">First 3 FREE</p>
              <p className="text-sm text-muted-foreground">Build trust & confidence</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Billing Cycle</p>
              <p className="text-xl font-bold">Weekly</p>
              <p className="text-sm text-muted-foreground">For qualified demos</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Revenue Share (Elite)</p>
              <p className="text-xl font-bold">5-10%</p>
              <p className="text-sm text-muted-foreground">After $50K+ new revenue</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Guarantee Period</p>
              <p className="text-xl font-bold">90 Days</p>
              <p className="text-sm text-muted-foreground">Risk-free partnership</p>
            </div>
          </div>
        </VentureCardContent>
      </VentureCard>

      {/* 90-Day Guarantee */}
      <VentureCard className="border-primary/30">
        <VentureCardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/20">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <VentureCardTitle>90-Day Guarantee Details</VentureCardTitle>
            <BadgeVenture variant="success">Risk-Free</BadgeVenture>
          </div>
        </VentureCardHeader>
        <VentureCardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-green-400">✓ What's Covered</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-400 mt-0.5" />
                  <span><strong>30+ demos:</strong> You're fully covered</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-400 mt-0.5" />
                  <span><strong>20-29 demos:</strong> Prorated protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-400 mt-0.5" />
                  <span><strong>&lt;20 demos:</strong> Full refund of demo fees</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-400 mt-0.5" />
                  <span>Must meet YOUR qualification criteria</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-400 mt-0.5" />
                  <span>Decision-maker or influencer contact</span>
                </li>
              </ul>
            </div>
            <div className="space-y-4">
              <h4 className="font-semibold text-red-400">✗ What's NOT Covered</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>No-shows after 2 confirmation attempts</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Leads that ghost post-demo (we did our job)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Your sales team's close rate (we bring qualified demos)</span>
                </li>
              </ul>
            </div>
          </div>
        </VentureCardContent>
      </VentureCard>

      {/* Target Markets */}
      <VentureCard>
        <VentureCardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Target className="w-6 h-6 text-purple-400" />
            </div>
            <VentureCardTitle>Target Markets</VentureCardTitle>
          </div>
        </VentureCardHeader>
        <VentureCardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 rounded-lg border border-border">
              <BadgeVenture variant="info" className="mb-2">Primary</BadgeVenture>
              <h3 className="text-xl font-bold mb-2">Healthcare SaaS</h3>
              <p className="text-muted-foreground">$100K+ MRR companies looking to scale demos and pipeline</p>
            </div>
            <div className="p-4 rounded-lg border border-border">
              <BadgeVenture variant="default" className="mb-2">Secondary</BadgeVenture>
              <h3 className="text-xl font-bold mb-2">Construction SaaS</h3>
              <p className="text-muted-foreground">Growing construction tech companies needing qualified leads</p>
            </div>
          </div>
        </VentureCardContent>
      </VentureCard>
    </motion.div>
  );
}
